import {
  Clock,
  RotateCcw,
  Wifi,
  WifiOff,
  ExternalLink,
  Check,
} from "../../../../components/icons";

interface CockpitHeaderProps {
  t: any;
  isConnected: boolean;
  queuedCount: number;
  failedCount: number;
  isSubmitting: boolean;
  isAdmin: boolean;
  openResetModal: () => void;
  resetBlocked: boolean;
  resetTooltip?: string;
  undoError: string | null;
  viewMode?: "logger" | "analytics" | "review";
  setViewMode?: (mode: "logger" | "analytics" | "review") => void;
  matchTimeSeconds: number;
  statusOverride?: string | null;
  matchStatus?: string;
  onShare?: () => void;
  shareCopied?: boolean;
}

export default function CockpitHeader({
  t,
  isConnected,
  queuedCount,
  failedCount,
  isSubmitting,
  isAdmin,
  openResetModal,
  resetBlocked,
  resetTooltip,
  undoError,
  matchTimeSeconds,
  statusOverride,
  matchStatus,
  onShare,
  shareCopied,
}: CockpitHeaderProps) {
  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-slate-100">{t("cockpit")}</h1>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <span
                data-testid="connection-status"
                data-status="connected"
                className="flex items-center gap-1.5 text-green-400 text-sm font-medium"
              >
                <Wifi size={16} aria-hidden="true" />
                {t("connected")}
              </span>
            ) : (
              <span
                data-testid="connection-status"
                data-status="disconnected"
                className="flex items-center gap-1.5 text-red-400 text-sm font-medium"
              >
                <WifiOff size={16} aria-hidden="true" />
                {t("disconnected")}
              </span>
            )}
            {queuedCount > 0 && (
              <span
                data-testid="queued-badge"
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800"
              >
                {queuedCount} {t("queued")}
              </span>
            )}
            {failedCount > 0 && (
              <span
                data-testid="failed-badge"
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-800"
                title={t(
                  "eventsFailedTooltip",
                  "Failed to save after repeated attempts. Not lost — recorded, but did not reach the server.",
                )}
              >
                {failedCount} {t("eventsFailed", "failed to save")}
              </span>
            )}
            {isSubmitting && (
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-primary-100 text-primary-800"
                data-testid="pending-ack-badge"
              >
                {t("waitingForServer", "Awaiting server confirmation…")}
              </span>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={openResetModal}
                data-testid="btn-reset-clock"
                className={`inline-flex items-center justify-center gap-1.5 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-xs min-h-[44px] ${
                  resetBlocked
                    ? "text-red-300 border border-red-900/40 cursor-not-allowed focus-visible:ring-red-500"
                    : "text-red-400 border border-red-700 hover:bg-red-900/30 active:bg-red-900/50 focus-visible:ring-red-500"
                }`}
                title={resetTooltip}
              >
                <RotateCcw size={14} aria-hidden="true" />
                {t("reset", "Reset")}
              </button>
            )}
            {isAdmin && onShare && (
              <button
                type="button"
                onClick={onShare}
                data-testid="btn-share-analytics"
                className={`inline-flex items-center justify-center gap-1.5 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-xs min-h-[44px] ${
                  shareCopied
                    ? "text-green-400 border border-green-600 focus-visible:ring-green-500"
                    : "text-sky-300 border border-sky-700 hover:bg-sky-900/30 active:bg-sky-900/50 focus-visible:ring-sky-500"
                }`}
                title={t("shareAnalytics", { ns: "logger" })}
              >
                {shareCopied ? (
                  <Check size={14} aria-hidden="true" />
                ) : (
                  <ExternalLink size={14} aria-hidden="true" />
                )}
                {shareCopied
                  ? t("shareLinkCopied", { ns: "logger" })
                  : t("shareAnalytics", { ns: "logger" })}
              </button>
            )}
          </div>
        </div>
        {undoError && (
          <p className="text-xs text-red-400 mt-1" data-testid="undo-error">
            {undoError}
          </p>
        )}

        <div className="flex items-center gap-4">
          <div className="text-sm text-slate-400 font-mono font-bold">
            <Clock className="inline mr-1" size={16} aria-hidden="true" />
            {Math.floor(Math.max(0, matchTimeSeconds || 0) / 60)}:
            {String(Math.max(0, matchTimeSeconds || 0) % 60).padStart(2, "0")}
          </div>
          {(() => {
            const statusForDisplay = (
              statusOverride ||
              matchStatus ||
              "Pending"
            ).toLowerCase();
            const colorClass =
              statusForDisplay === "live" ||
              statusForDisplay === "live_first_half"
                ? "bg-green-100 text-green-800"
                : statusForDisplay === "halftime"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-700";
            return (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colorClass}`}
              >
                {t(`status.${statusForDisplay}`)}
              </span>
            );
          })()}
        </div>
      </div>
    </>
  );
}
